<template>
  <USModal
    :is-open="isOpen"
    :slot-overflow-visible="true"
    class="add-event-modal"
    @update:is-open="emit('update:isOpen', $event)"
  >
    <h3 class="add-event-modal__title heading--h3 margin-bottom--7">
      Add event
    </h3>
    <USInputText
      v-model="eventName"
      class="add-event-modal__field"
      heading="Event name"
      :heading-level="4"
      placeholder="e.g. Spring Member–Guest"
      input-id="add-event-name"
      name="add-event-name"
      minlength="1"
      maxlength="255"
      :error="eventNameError"
    />
    <div class="add-event-modal__field margin-top--6">
      <div class="add-event-modal__field-heading display--d2--semibold">
        <h4>Select club</h4>
      </div>
      <ClubSearchSelect
        v-model="selectedClubId"
        :error="clubSelectionError"
        :preset-club-id="pickerPresetId"
        :preset-club-name="pickerPresetName"
        :preset-club-is-non-golf="pickerPresetNonGolf"
        @update:club-is-non-golf="selectedClubIsNonGolf = $event"
      />
    </div>
    <div class="add-event-modal__footer margin-top--4">
      <USButton
        is="button"
        type="inline"
        label="Cancel"
        @click="closeModal"
      />
      <USButton
        is="button"
        label="Save"
        :disabled="savePending"
        @click="handleSave"
      />
    </div>
  </USModal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import USButton from '@base/components/USButton/USButton.vue'
import USInputText from '@base/components/USInputText/USInputText.vue'
import USModal from '@base/components/USModal/USModal.vue'

import ClubSearchSelect from './ClubSearchSelect.vue'

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    /** Disables Save while the parent runs the create mutation. */
    savePending?: boolean
    /** When set with initialClubName, pre-fills and selects the club (list stays closed until the user edits search). */
    initialClubId?: string | null
    initialClubName?: string | null
    initialClubIsNonGolf?: boolean | null
  }>(),
  {
    savePending: false,
    initialClubId: null,
    initialClubName: null,
    initialClubIsNonGolf: null
  }
)

const pickerPresetId = computed(() =>
  props.isOpen &&
  props.initialClubId &&
  (props.initialClubName?.trim() ?? '').length > 0
    ? props.initialClubId
    : null
)

const pickerPresetName = computed(() =>
  props.isOpen &&
  props.initialClubId &&
  (props.initialClubName?.trim() ?? '').length > 0
    ? props.initialClubName!.trim()
    : null
)

const pickerPresetNonGolf = computed(() =>
  pickerPresetId.value != null ? props.initialClubIsNonGolf ?? false : null
)

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
  (e: 'save', payload: { clubId: string; eventName: string; isNonGolf: boolean }): void
}>()

const selectedClubId = ref<string | null>(null)
const selectedClubIsNonGolf = ref<boolean | null>(null)
const eventName = ref('')
const eventNameError = ref('')
const clubSelectionError = ref('')

function closeModal() {
  emit('update:isOpen', false)
}

function resetForm() {
  selectedClubId.value = null
  selectedClubIsNonGolf.value = null
  eventName.value = ''
  eventNameError.value = ''
  clubSelectionError.value = ''
}

function handleSave() {
  eventNameError.value = ''
  clubSelectionError.value = ''

  const trimmed = eventName.value.trim()
  let ok = true

  if (!trimmed) {
    eventNameError.value = 'This field is required'
    ok = false
  }

  if (!selectedClubId.value) {
    clubSelectionError.value = 'Please select a club.'
    ok = false
  }

  if (!ok) return

  emit('save', {
    clubId: selectedClubId.value!,
    eventName: trimmed,
    isNonGolf: selectedClubIsNonGolf.value ?? false
  })
}

watch(
  () => props.isOpen,
  (open) => {
    if (!open) {
      resetForm()
      return
    }
    if (props.initialClubId) {
      selectedClubId.value = props.initialClubId
      if (props.initialClubIsNonGolf != null) {
        selectedClubIsNonGolf.value = props.initialClubIsNonGolf
      }
    }
  }
)

watch(selectedClubId, () => {
  if (selectedClubId.value) clubSelectionError.value = ''
})

watch(eventName, () => {
  if (eventName.value.trim()) eventNameError.value = ''
})
</script>

<style lang="scss" scoped>
.add-event-modal {
  text-align: left;

  &__field-heading {
    margin-bottom: var(--spacing--9);

    h4 {
      margin: 0;
    }
  }

  &__footer {
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
}
</style>
